import React, { useState, useEffect } from 'react';
import { FiX, FiArrowRight, FiSmile } from 'react-icons/fi';
import '../styles/welcomesplash.css';

// Diwali festive greetings are displayed up to and including 10th November 2026.
// From 11th November 2026 onwards, the festive modal automatically removes and restores the classic welcome modal.
const DIWALI_EXPIRY_DATE = new Date('2026-11-10T23:59:59+05:30');

const WelcomeSplash = () => {
  const [show, setShow] = useState(false);
  const [userName, setUserName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Check if current date is within Diwali festive season (up through 10 Nov 2026)
  const isDiwaliActive = new Date() <= DIWALI_EXPIRY_DATE;

  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    
    if (!hasSeenSplash) {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          if (userObj) {
            if (userObj.name) setUserName(userObj.name);
            if (userObj.business_name) setBusinessName(userObj.business_name);
          }
        }
      } catch (e) {}

      setShow(true);
      sessionStorage.setItem('hasSeenSplash', 'true');
      
      // Auto fade-out & close:
      // - Diwali festive modal: 7s fade, 7.8s close
      // - Classic welcome modal: 2.5s fade, 3.5s close
      const fadeDuration = isDiwaliActive ? 7000 : 2500;
      const closeDuration = isDiwaliActive ? 7800 : 3500;

      const timerFade = setTimeout(() => {
        setIsFadingOut(true);
      }, fadeDuration);
      
      const timerClose = setTimeout(() => {
        setShow(false);
      }, closeDuration);

      return () => {
        clearTimeout(timerFade);
        clearTimeout(timerClose);
      };
    }
  }, [isDiwaliActive]);

  const handleClose = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      setShow(false);
    }, 450);
  };

  if (!show) return null;

  // Render Classic Welcome Modal after 10th November 2026
  if (!isDiwaliActive) {
    return (
      <div 
        className={`welcome-splash-overlay classic-theme ${isFadingOut ? 'fade-out' : 'fade-in'}`}
        onClick={(e) => {
          if (e.target.classList.contains('welcome-splash-overlay')) {
            handleClose();
          }
        }}
      >
        <div className="welcome-splash-card">
          <div className="splash-icon-wrapper">
            <FiSmile className="splash-icon" />
          </div>
          <h1 className="welcome-splash-title">
            Welcome back{userName ? `, ${userName}` : ''}!
          </h1>
          <p className="welcome-splash-subtitle">
            Success is built one step at a time. Let's make today productive.
          </p>
          <div className="splash-loading-bar-container">
            <div className="splash-loading-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  // Render Diwali Special Festive Modal (Diwali on 8th November 2026, active through 10th November 2026)
  return (
    <div 
      className={`welcome-splash-overlay ${isFadingOut ? 'fade-out' : 'fade-in'}`}
      onClick={(e) => {
        if (e.target.classList.contains('welcome-splash-overlay')) {
          handleClose();
        }
      }}
    >
      <div className="diwali-welcome-card">
        {/* Close Button */}
        <button 
          className="diwali-modal-close-btn" 
          onClick={handleClose} 
          aria-label="Close Diwali Wishes"
        >
          <FiX size={18} />
        </button>

        {/* Decorative Top Toran Arch SVG */}
        <div className="diwali-card-toran">
          <svg viewBox="0 0 500 45" fill="none" preserveAspectRatio="none" className="toran-svg">
            {/* Hanging marigold garlands */}
            <path d="M0 4 Q62 30 125 4 Q187 30 250 4 Q312 30 375 4 Q437 30 500 4" stroke="#f59e0b" strokeWidth="2.5" fill="none" />
            <path d="M0 6 Q62 26 125 6 Q187 26 250 6 Q312 26 375 6 Q437 26 500 6" stroke="#ea580c" strokeWidth="1" strokeDasharray="3,3" fill="none" />
            
            {/* Hanging Mango Leaves */}
            <path d="M62 18 C58 28, 66 34, 62 42 C58 34, 66 28, 62 18 Z" fill="#15803d" />
            <path d="M187 18 C183 28, 191 34, 187 42 C183 34, 191 28, 187 18 Z" fill="#15803d" />
            <path d="M312 18 C308 28, 316 34, 312 42 C308 34, 316 28, 312 18 Z" fill="#15803d" />
            <path d="M437 18 C433 28, 441 34, 437 42 C433 34, 441 28, 437 18 Z" fill="#15803d" />

            {/* Marigold Flower Nodes */}
            <circle cx="0" cy="4" r="5" fill="#f59e0b" />
            <circle cx="125" cy="4" r="5" fill="#ea580c" />
            <circle cx="250" cy="4" r="6" fill="#f59e0b" />
            <circle cx="375" cy="4" r="5" fill="#ea580c" />
            <circle cx="500" cy="4" r="5" fill="#f59e0b" />
          </svg>
        </div>

        {/* Floating Festive Sparkles */}
        <div className="diwali-sparkle-dot sparkle-1">✦</div>
        <div className="diwali-sparkle-dot sparkle-2">★</div>
        <div className="diwali-sparkle-dot sparkle-3">✦</div>
        <div className="diwali-sparkle-dot sparkle-4">★</div>

        {/* Date & Occasion Pill Badge: Diwali is on 8th November 2026 */}
        <div className="diwali-date-badge">
          <span className="diwali-badge-sparkle">✨</span>
          <span className="diwali-badge-text">DIWALI SPECIAL · 8TH NOVEMBER 2026</span>
          <span className="diwali-badge-sparkle">✨</span>
        </div>

        {/* Twin Flaming Diya Artwork */}
        <div className="diwali-artwork-container">
          <svg className="diwali-central-diya" width="90" height="74" viewBox="0 0 90 74" fill="none">
            <defs>
              <radialGradient id="flameAura" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fef08a" stopOpacity="0.9" />
                <stop offset="45%" stopColor="#f59e0b" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="brassBase" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="40%" stopColor="#d97706" />
                <stop offset="100%" stopColor="#78350f" />
              </linearGradient>
            </defs>

            {/* Glowing Flame Aura */}
            <circle cx="45" cy="24" r="22" fill="url(#flameAura)" className="diwali-flame-glow" />

            {/* Animated Flames */}
            <g className="diwali-animated-flame">
              {/* Outer Flame */}
              <path d="M45 4 C34 16, 37 28, 45 35 C53 28, 56 16, 45 4 Z" fill="#ea580c" />
              {/* Mid Golden Flame */}
              <path d="M45 11 C39 20, 41 27, 45 32 C49 27, 51 20, 45 11 Z" fill="#f59e0b" />
              {/* Inner Bright Core */}
              <path d="M45 18 C42 24, 43 28, 45 30 C47 28, 48 24, 45 18 Z" fill="#fef08a" />
            </g>

            {/* Diya Base / Oil Lamp */}
            <path d="M12 48 C12 68, 78 68, 78 48 C78 45, 12 45, 12 48 Z" fill="url(#brassBase)" stroke="#92400e" strokeWidth="1.2" />
            <path d="M18 47 C18 61, 72 61, 72 47 Z" fill="#b45309" opacity="0.6" />
            <ellipse cx="45" cy="46" rx="31" ry="6" fill="#78350f" />
            <ellipse cx="45" cy="45" rx="27" ry="4.5" fill="#f59e0b" />
            
            {/* Diya Stand */}
            <path d="M36 65 L54 65 L57 71 L33 71 Z" fill="url(#brassBase)" stroke="#78350f" strokeWidth="1" />
          </svg>
        </div>

        {/* Sacred Traditional Greeting */}
        <div className="diwali-hindi-title">शुभ दीपावली</div>
        <h1 className="diwali-english-title">Happy Diwali!</h1>

        {/* Welcome Back User Banner */}
        <div className="diwali-welcome-banner">
          <span className="diwali-sparkle-mini">🪔</span>
          <span className="diwali-welcome-name">
            Welcome back{userName ? `, ${userName}` : ''}!
          </span>
          {businessName && (
            <span className="diwali-business-tag">({businessName})</span>
          )}
          <span className="diwali-sparkle-mini">🪔</span>
        </div>

        {/* Heartfelt Festive Business Blessing */}
        <p className="diwali-blessing-text">
          On this auspicious occasion of <strong>Diwali (8th November 2026)</strong>, may the divine blessings of 
          <strong> Goddess Lakshmi &amp; Lord Ganesha</strong> bring boundless prosperity, good fortune, 
          and soaring success to your business and family.
        </p>

        {/* Auspicious Shubh Labh Footer Flourish */}
        <div className="diwali-flourish-ribbon">
          <span>✦ शुभ</span>
          <span className="ribbon-dot">•</span>
          <span>सुख</span>
          <span className="ribbon-dot">•</span>
          <span>समृद्धि</span>
          <span className="ribbon-dot">•</span>
          <span>लाभ ✦</span>
        </div>

        {/* Interactive Action Button */}
        <div className="diwali-actions-container">
          <button 
            className="diwali-continue-btn"
            onClick={handleClose}
          >
            <span>Celebrate &amp; Continue to Dashboard</span>
            <FiArrowRight size={17} />
          </button>
        </div>

        {/* Auto-Dismiss Golden Progress Bar */}
        <div className="diwali-progress-bar-container" title="Auto-closing in 7s">
          <div className="diwali-progress-fill"></div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeSplash;

